import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/kit';
import apiClient from '@/services/apiClient';
import { money } from '../lib/scope';

interface RunLine {
  personName: string;
  lane: 'doctors' | 'staff';
  netMinor: number;
  deductionsMinor: number;
  reverse: boolean;
}
interface RunView {
  status: string;
  sentBy: string | null;
  grossMinor: number;
  deductionsMinor: number;
  netMinor: number;
  toCollectMinor: number;
  cutOff: string;
  lines: RunLine[];
}

/** The month's run, as the accountant sent it — `GET /payroll?period=`. */
export function usePayoutRun(period: string | null) {
  const q = useQuery({
    queryKey: ['cm-payout-run', period],
    enabled: !!period && /^\d{4}-\d{2}$/.test(period),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<RunView | null> => {
      const res = await apiClient.get('/payroll', { params: { period } });
      return (res.data?.data ?? res.data ?? null) as RunView | null;
    },
  });
  return q.data ?? null;
}

const k = (minor: number) => Math.round(Math.abs(minor) / 100).toLocaleString();

/** g_alerts' pane for a payout run: prepared by · the run · who is in it. */
export function PayoutRunDetail({ period, requestedBy }: { period: string; requestedBy?: string | null }) {
  const run = usePayoutRun(period);
  if (!run) return null;
  const doctors = run.lines.filter((l) => l.lane === 'doctors');
  const staff = run.lines.filter((l) => l.lane === 'staff');
  const sum = (ls: RunLine[]) => ls.reduce((n, l) => n + l.netMinor, 0);
  const who = (l: RunLine) => `${l.personName} ${l.reverse ? '−' : ''}${k(l.netMinor)}`;
  return (
    <>
      <Card>
        <p className="t-ovl mut">Prepared by</p>
        <p className="t-sec sec mt4">
          {requestedBy ?? run.sentBy ?? 'the accountant'} · {run.lines.length} {run.lines.length === 1 ? 'person' : 'people'}
        </p>
        <p className="t-cap mut">the accountant prepares and sends; only you approve and settle</p>
      </Card>
      <Card>
        <p className="t-ovl mut">The run</p>
        <p className="t-sec sec mt4">
          gross {k(run.grossMinor)} · deductions −{k(run.deductionsMinor)} · net to pay {k(run.netMinor)}
        </p>
        <p className="t-cap mut">
          doctors {k(sum(doctors))} + staff {k(sum(staff))} · cut-off {run.cutOff}
        </p>
      </Card>
      <Card>
        <p className="t-ovl mut">Who is in it</p>
        <p className="t-sec sec mt4">{run.lines.map(who).join(' · ') || 'nobody yet'}</p>
        <p className="t-cap mut">
          the {run.lines.length} net rows sum to {money(run.netMinor / 100)}
        </p>
      </Card>
    </>
  );
}
